import { apiClient, createAuthHeaders } from '../api-client';
import { auth } from '../firebase';
import type {
  SkillsCatalogResponse,
  MarketplaceSkillFull,
  InstalledSkill,
  SkillsCategoryCount,
  CustomSkillItem,
  CreateCustomSkillPayload,
  UpdateCustomSkillPayload,
} from '@optidev/shared-types';

/**
 * Slug used to reference a custom skill in mention chips (`custom-{slug}`).
 * Must stay in sync between chip creation (skill picker) and chip navigation
 * (mention badge), which resolves the slug back to the custom skill's DB id.
 */
export function slugifySkillName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

async function getToken(): Promise<string> {
  // Use the embed-aware auth wrapper: in scio3 embed mode it fetches a fresh
  // token from the parent via the ON_GET_TOKEN bridge; in normal mode it falls
  // through to firebaseAuth.currentUser. firebaseAuth.currentUser is always null
  // in embed mode, so reading it directly broke the marketplace when embedded.
  const token = await auth.getIdToken();
  if (!token) throw new Error('Not authenticated');
  return token;
}

export const skillsMarketplaceApi = {
  async getCatalog(params?: {
    search?: string;
    category?: string;
    featured?: boolean;
    popular?: boolean;
    page?: number;
    limit?: number;
  }): Promise<SkillsCatalogResponse> {
    const token = await getToken();
    const response = await apiClient.get('/api/skills-marketplace/catalog', {
      headers: createAuthHeaders(token),
      params,
    });
    return response.data;
  },

  async getCategories(): Promise<SkillsCategoryCount[]> {
    const token = await getToken();
    const response = await apiClient.get(
      '/api/skills-marketplace/catalog/categories',
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async getSkillDetail(id: string): Promise<MarketplaceSkillFull> {
    const token = await getToken();
    const response = await apiClient.get(
      `/api/skills-marketplace/catalog/${id}`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async getInstalled(): Promise<InstalledSkill[]> {
    const token = await getToken();
    const response = await apiClient.get('/api/skills-marketplace/installed', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async install(skillId: string): Promise<{ success: boolean; installedAt: string }> {
    const token = await getToken();
    const response = await apiClient.post(
      '/api/skills-marketplace/install',
      { skillId },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async uninstall(skillId: string): Promise<{ success: boolean }> {
    const token = await getToken();
    const response = await apiClient.post(
      '/api/skills-marketplace/uninstall',
      { skillId },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async getCustomSkills(): Promise<CustomSkillItem[]> {
    const token = await getToken();
    const response = await apiClient.get('/api/skills-marketplace/custom', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async getCustomSkill(id: string): Promise<CustomSkillItem> {
    const token = await getToken();
    const response = await apiClient.get(`/api/skills-marketplace/custom/${id}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async createCustomSkill(dto: CreateCustomSkillPayload): Promise<CustomSkillItem> {
    const token = await getToken();
    const response = await apiClient.post('/api/skills-marketplace/custom', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async updateCustomSkill(id: string, dto: UpdateCustomSkillPayload): Promise<CustomSkillItem> {
    const token = await getToken();
    const response = await apiClient.patch(`/api/skills-marketplace/custom/${id}`, dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async deleteCustomSkill(id: string): Promise<{ success: boolean }> {
    const token = await getToken();
    const response = await apiClient.delete(`/api/skills-marketplace/custom/${id}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },
};
